import { useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { landings } from "@/data/landings";
import { PageCTA } from "@/components/wve/PageCTA";
import { setPageMeta, resetPageMeta } from "@/lib/seo";

const setMeta = (title, desc, lp, slug) => {
  setPageMeta(title, desc, `/${slug}`);
  let ld = document.getElementById("lp-jsonld");
  if (!ld) {
    ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "lp-jsonld";
    document.head.appendChild(ld);
  }
  ld.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Service",
    name: lp.label,
    description: desc,
    provider: {
      "@type": "LocalBusiness",
      name: "WVE Studios",
      telephone: "01277 280 512",
      email: "alex@wvestudios.co.uk",
      address: { "@type": "PostalAddress", streetAddress: "Unit 6 Hodgson Court, Hodgson Way", addressLocality: "Wickford", addressRegion: "Essex", postalCode: "SS11 8XR", addressCountry: "GB" },
    },
    areaServed: "Essex, United Kingdom",
    url: `https://www.wvestudios.co.uk/${slug}`,
  });
};

export default function LandingPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+/, "").split("/")[0];
  const lp = landings[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (lp) setMeta(lp.seoTitle, lp.seoDesc, lp, slug);
    return () => {
      resetPageMeta();
      document.getElementById("lp-jsonld")?.remove();
    };
  }, [slug, lp]);

  if (!lp) return <Navigate to="/" replace />;

  return (
    <main data-testid={`landing-page-${slug}`}>
      <section className="bg-[#0A0A0A] text-white pt-32 sm:pt-40 pb-20 relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-4">{lp.label} — Essex</p>
            <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-6xl lg:text-7xl tracking-tight">
              {lp.titleA}<br /><span className="text-[#E80042]">{lp.titleB}</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed">{lp.intro[0]}</p>
            {lp.price && (
              <div className="mt-7 inline-block bg-[#E80042] rounded-lg px-6 py-4" data-testid="landing-price-card">
                <span className="font-display text-3xl uppercase">{lp.price}</span>
                <p className="text-white/90 text-sm mt-1 max-w-xs">{lp.priceNote}</p>
              </div>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-5">
            <img src={lp.heroImg} alt={lp.label} className="w-full h-[420px] object-cover rounded-lg -rotate-1" />
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 space-y-6">
          {lp.intro.slice(1).map((p, i) => (
            <p key={i} className={i === 0 ? "font-grotesk font-bold text-2xl sm:text-3xl leading-snug" : "text-lg text-[#555555] leading-relaxed"}>{p}</p>
          ))}
        </div>
      </section>

      {lp.sections.map((s, i) => (
        <section key={s.title} className={`py-16 sm:py-20 ${i % 2 === 0 ? "bg-white border-y border-black/10" : ""}`} data-testid={`landing-section-${i + 1}`}>
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-6">
              <span className="text-[#E80042] mr-3">{String(i + 1).padStart(2, "0")}</span>
              {s.title}
            </h2>
            <div className="space-y-4 mb-8">
              {s.paragraphs.map((p, j) => (
                <p key={j} className="text-lg text-[#555555] leading-relaxed">{p}</p>
              ))}
            </div>
            {s.video && (
              <div className="mb-4">
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E80042] mb-3">▶ {s.videoLabel}</p>
                <div className="aspect-video rounded-lg overflow-hidden bg-[#0A0A0A]">
                  <iframe src={`https://www.youtube.com/embed/${s.video}?rel=0`} title={s.videoLabel} className="w-full h-full border-0" allow="autoplay; fullscreen; encrypted-media" />
                </div>
              </div>
            )}
            {s.images && (
              <div className="grid sm:grid-cols-2 gap-5">
                {s.images.map((img) => (
                  <img key={img} src={img} alt={`${lp.label} — ${s.title}`} className="w-full h-[280px] object-cover rounded-lg" />
                ))}
              </div>
            )}
          </div>
        </section>
      ))}

      <PageCTA context={lp.cta} title="Get more information." subtitle="Call us or send an enquiry — we'll come back with a bespoke quote." />
    </main>
  );
}
